import { cloneDeep, get, isArray, set } from 'lodash';
import { KOSTEN_ARTEN, KOSTEN_POSITION, KOSTENTRAEGER } from '../../../../../lib/constants/constant';
import { FormulasResultType } from '../../../../../lib/types/types';
import { addIndexesToPath, getFieldNamefromDataPath, groupKostenByPosition, isAntragstellerHAW, isTrue } from '../../../../../utils/helper';
import { getBasisDataKostenKategorie } from '../../application-helper-function/AppHelperfunctions';
import { calculateProjektDauer, calculateProjektStartJahr, ersteVerbundPartnerAnlegen } from '../commonFormulas';

// import schemas
import AntragSchema from '../../../../../lib/paths/antrag_FULL_Paths.json';
import KostenPositionSchema from '../../../../../lib/paths/kostenposition_FULL_Paths.json';
const P = AntragSchema.attributes;
const KP_P = KostenPositionSchema.attributes;

export const executeLoeweSkizzeFormulas = (
	application: any,
	kostenKategories: any,
	vonTag: number,
	vonMonat: string,
	bisTag: number,
	bisMonat: string,
) => {
	let appCopy = cloneDeep(application);
	let result: FormulasResultType[] = [];
	const KostenKategoriesGroup = groupKostenByPosition(kostenKategories);

	let calculatedProjektJahr = calculateProjektStartJahr(application, vonTag, vonMonat);
	if (isArray(calculatedProjektJahr)) {
		for (let ele of calculatedProjektJahr) {
			set(appCopy, get(ele, 'path'), get(ele, 'value'));
		}
	}
	let calculatedProjektDauer = calculateProjektDauer(application, vonTag, vonMonat, bisTag, bisMonat);
	if (isArray(calculatedProjektDauer)) {
		for (let ele of calculatedProjektDauer) {
			set(appCopy, get(ele, 'path'), get(ele, 'value'));
		}
	}
	result.push(...calculatedProjektJahr);
	result.push(...calculatedProjektDauer);

	let erstePartnerAnlegen = ersteVerbundPartnerAnlegen(appCopy);
	if (isArray(erstePartnerAnlegen)) {
		for (let ele of erstePartnerAnlegen) {
			set(appCopy, get(ele, 'path'), get(ele, 'value'));
		}
	}
	result.push(...erstePartnerAnlegen);

	if (get(appCopy, P.Allgemein_Projektdauerin_Jahren.path) > 0) {
		if (get(KostenKategoriesGroup, KOSTEN_POSITION.Ausgabe, []).includes(KOSTEN_ARTEN.Personalkosten)) {
			let calculatedPersonalKosten = calculatePersonalKostenSkizzeLoewe(appCopy);
			if (isArray(calculatedPersonalKosten)) {
				for (let ele of calculatedPersonalKosten) {
					set(appCopy, get(ele, 'path'), get(ele, 'value'));
				}
			}
			result.push(...calculatedPersonalKosten);

			let calculatedAdminPauschale = calculateAdministrationspauschaleSkizzeLoewe(appCopy, kostenKategories);
			if (isArray(calculatedAdminPauschale)) {
				for (let ele of calculatedAdminPauschale) {
					set(appCopy, get(ele, 'path'), get(ele, 'value'));
				}
			}
			result.push(...calculatedAdminPauschale);
		}

		let calculatedSummePriOrganisation = calculateSummeProOrganisationSkizzeLoewe(appCopy);
		if (isArray(calculatedSummePriOrganisation)) {
			for (let ele of calculatedSummePriOrganisation) {
				set(appCopy, get(ele, 'path'), get(ele, 'value'));
			}
		}
		result.push(...calculatedSummePriOrganisation);

		let calculatedGesamtSummeSkizzeLoewe = calculateGesamtSummeSkizzeLoewe(appCopy);
		if (isArray(calculatedGesamtSummeSkizzeLoewe)) {
			for (let ele of calculatedGesamtSummeSkizzeLoewe) {
				set(appCopy, get(ele, 'path'), get(ele, 'value'));
			}
		}
		result.push(...calculatedGesamtSummeSkizzeLoewe);

		let calculatedJahresaufteilung = calculateJahresAufteilung(appCopy);
		if (isArray(calculatedJahresaufteilung)) {
			for (let ele of calculatedJahresaufteilung) {
				set(appCopy, get(ele, 'path'), get(ele, 'value'));
			}
		}
		result.push(...calculatedJahresaufteilung);
	}

	return result;
};

function calculatePersonalKostenSkizzeLoewe(application: any) {
	let result = [];

	const allKostenPosition = get(application, P.Foerderantrag_Kostenpositions.path, []);
	for (let i = 0; i < allKostenPosition.length; i++) {
		const eachKostenPosition = allKostenPosition[i];
		if (
			get(eachKostenPosition, KP_P.Allgemein_Kostenart_Finanzierung.path) === KOSTEN_ARTEN.Personalkosten &&
			!isTrue(get(eachKostenPosition, `${KP_P.Personalausgaben_LOEWE_Antrag.schema.attributes.Administrationspauschale.path}`, false))
		) {
			const StundenSatz = get(eachKostenPosition, KP_P.Personalausgaben_LOEWE_Skizze.schema.attributes.Stundensatz.path, 0);
			const Geplantestunde = get(eachKostenPosition, KP_P.Personalausgaben_LOEWE_Skizze.schema.attributes.Geplante_Stunden_Im_Projekt.path, 0);

			const geplaneSumme = parseFloat(StundenSatz) * parseFloat(Geplantestunde);
			result.push({
				path: addIndexesToPath(
					P.Foerderantrag_Kostenpositions.schema.attributes.Allgemein_Geplante_Gesamtsumme.path,
					[`${i}`],
					['Foerderantrag_Kostenpositions'],
				),
				value: isFinite(geplaneSumme) ? geplaneSumme.toFixed(2) : 0,
			});
		}
	}
	return result;
}

function calculateAdministrationspauschaleSkizzeLoewe(application: any, kostenKategories: any) {
	let result: FormulasResultType[] = [];

	const orgaDaten = get(application, P.Antragstellende_Organisation.path, {});
	if (!isAntragstellerHAW(orgaDaten)) {
		// wenn Antragsteller kein HAW ist, muss NICHTS gemacht werden.
		return result;
	}

	const unternehmenId = get(application, `${P.Antragstellende_Organisation.path}.id`);
	const projektStartJahr = get(application, P.Allgemein_Projektstart_Jahr.path, 0);
	const allKostenPositionen = get(application, P.Foerderantrag_Kostenpositions.path, []);

	// 1. Alle Personalkosten des Antragstellers summieren (ohne Administrationspauschale)
	const personalKostenAntragsteller = allKostenPositionen.filter(
		(ele: any) =>
			get(ele, KP_P.Allgemein_Kostenposition.path) === KOSTEN_POSITION.Ausgabe &&
			get(ele, KP_P.Allgemein_Kostenart_Finanzierung.path) === KOSTEN_ARTEN.Personalkosten &&
			get(ele, `${KP_P.Unternehmen.path}.id`) === unternehmenId &&
			get(ele, KP_P.Allgemein_Kostentraeger.path) === KOSTENTRAEGER.Antragsteller &&
			!get(ele, KP_P.Personalausgaben_LOEWE_Antrag.schema.attributes.Administrationspauschale.path, false),
	);

	// Gesamtsumme der Personalkosten für Antragsteller berechnen
	const personalkostenGesamt = personalKostenAntragsteller.reduce((sum: number, ele: any) => {
		return sum + parseFloat(get(ele, KP_P.Allgemein_Geplante_Gesamtsumme.path, '0.0'));
	}, 0);

	// 3. 20% pro Jahr berechnen
	const gesamtSumme = parseFloat((personalkostenGesamt * 0.2).toFixed(2)).toFixed(2);
	const kostenProJahr = [
		{
			Zelle_1: projektStartJahr,
			Zelle_2: gesamtSumme,
		},
	];

	// 4. Prüfen, ob bereits eine Administrationspauschale existiert
	const existingIndex = allKostenPositionen.findIndex(
		(ele: any) => get(ele, KP_P.Personalausgaben_LOEWE_Antrag.schema.attributes.Administrationspauschale.path, false) === true,
	);

	if (existingIndex >= 0) {
		// Bestehende Kostenposition aktualisieren
		result.push({
			path: addIndexesToPath(
				P.Foerderantrag_Kostenpositions.schema.attributes.Allgemein_Kosten_Pro_Jahr.path,
				[`${existingIndex}`],
				['Foerderantrag_Kostenpositions'],
			),
			value: kostenProJahr,
		});
		result.push({
			path: addIndexesToPath(
				P.Foerderantrag_Kostenpositions.schema.attributes.Allgemein_Geplante_Gesamtsumme.path,
				[`${existingIndex}`],
				['Foerderantrag_Kostenpositions'],
			),
			value: gesamtSumme,
		});
		result.push({
			path: addIndexesToPath(
				P.Foerderantrag_Kostenpositions.schema.attributes.Personalausgaben_LOEWE_Antrag.path,
				[`${existingIndex}`],
				['Foerderantrag_Kostenpositions'],
			),
			value: {
				Administrationspauschale: true,
				Name_Desder_Beschaeftigten: 'Administrationspauschale (anteilig max. 20% der Personalausgaben)',
			},
		});
	} else {
		// Neue Kostenposition anlegen
		// Kostenkategorie aus einer existierenden Personalkosten-Position des Antragstellers holen
		const allKostenkategorieForPersonal = kostenKategories.filter(
			(ele: any) => get(ele, 'attributes.Allgemein_Kostenart_Finanzierung') === KOSTEN_ARTEN.Personalkosten,
		);

		const data = getBasisDataKostenKategorie(
			application,
			allKostenkategorieForPersonal,
			KOSTENTRAEGER.Antragsteller,
			unternehmenId,
			KOSTEN_POSITION.Ausgabe,
			KOSTEN_ARTEN.Personalkosten,
		);

		set(data, getFieldNamefromDataPath(KP_P.Allgemein_Bezeichnung.path), 'Administrationspauschale (anteilig max. 20% der Personalausgaben)');
		set(data, getFieldNamefromDataPath(KP_P.Personalausgaben_LOEWE_Antrag.path), {
			Administrationspauschale: true,
			Name_Desder_Beschaeftigten: 'Administrationspauschale (anteilig max. 20% der Personalausgaben)',
		});
		set(data, getFieldNamefromDataPath(KP_P.Allgemein_Kosten_Pro_Jahr.path), kostenProJahr);
		set(data, getFieldNamefromDataPath(KP_P.Allgemein_Geplante_Gesamtsumme.path), gesamtSumme);

		const newIndex = allKostenPositionen.length;
		result.push({
			path: addIndexesToPath(P.Foerderantrag_Kostenpositions.path, [`${newIndex}`], ['Foerderantrag_Kostenpositions']),
			value: { attributes: data },
		});
	}

	return result;
}

function calculateSummeProOrganisationSkizzeLoewe(application: any) {
	let result: FormulasResultType[] = [];
	const allKostenPosition = get(application, P.Foerderantrag_Kostenpositions.path, []);

	const allAusgabenAntragsteller_Arr = allKostenPosition.filter(
		(ele: any) =>
			get(ele, `${KP_P.Unternehmen.path}.id`) === get(application, `${P.Antragstellende_Organisation.path}.id`) &&
			get(ele, KP_P.Allgemein_Kostenposition.path) === 'Ausgabe',
	);
	const allEinnahmenAntragsteller_Arr = allKostenPosition.filter(
		(ele: any) =>
			get(ele, `${KP_P.Unternehmen.path}.id`) === get(application, `${P.Antragstellende_Organisation.path}.id`) &&
			get(ele, KP_P.Allgemein_Kostenposition.path) === 'Einnahme',
	);

	const ausgabenGesamtAntragsteller = allAusgabenAntragsteller_Arr.reduce((acc: any, ele: any) => {
		return acc + parseFloat(get(ele, KP_P.Allgemein_Geplante_Gesamtsumme.path, '0.0'));
	}, 0);
	const einnahmenGesamtAntragsteller = allEinnahmenAntragsteller_Arr.reduce((acc: any, ele: any) => {
		return acc + parseFloat(get(ele, KP_P.Allgemein_Geplante_Gesamtsumme.path, '0.0'));
	}, 0);
	const zuwendungGesamtAntragsteller = ausgabenGesamtAntragsteller - einnahmenGesamtAntragsteller;
	const foerderQuoteAntragsteller = (zuwendungGesamtAntragsteller / ausgabenGesamtAntragsteller) * 100;

	result.push({
		path: P.Orga_Foerderquote_Gesamt.path,
		value: isFinite(foerderQuoteAntragsteller) ? foerderQuoteAntragsteller.toFixed(2) : 0,
	});

	const allVerbundPartner = get(application, P.Verbundpartner.path, []);

	allVerbundPartner.forEach((eachPartner: any, i: number) => {
		const eachPartnerId = get(eachPartner, 'Verbundpartner.id');
		if (eachPartnerId) {
			const allAusgabenEachPartner_Arr = allKostenPosition.filter(
				(ele: any) => get(ele, `${KP_P.Unternehmen.path}.id`) === eachPartnerId && get(ele, KP_P.Allgemein_Kostenposition.path) === 'Ausgabe',
			);
			const allEinnahmenEachPartnerr_Arr = allKostenPosition.filter(
				(ele: any) => get(ele, `${KP_P.Unternehmen.path}.id`) === eachPartnerId && get(ele, KP_P.Allgemein_Kostenposition.path) === 'Einnahme',
			);

			const ausgabenGesamtEachPartner = allAusgabenEachPartner_Arr.reduce((acc: any, ele: any) => {
				return acc + parseFloat(get(ele, KP_P.Allgemein_Geplante_Gesamtsumme.path, '0.0'));
			}, 0);
			const einnahmenGesamtEachPartner = allEinnahmenEachPartnerr_Arr.reduce((acc: any, ele: any) => {
				return acc + parseFloat(get(ele, KP_P.Allgemein_Geplante_Gesamtsumme.path, '0.0'));
			}, 0);
			const zuwendungGesamtEachPartner = ausgabenGesamtEachPartner - einnahmenGesamtEachPartner;
			const foerderQuoteEachPartner = (zuwendungGesamtEachPartner / ausgabenGesamtEachPartner) * 100;
			result.push({
				path: `${P.Verbundpartner.path}.${i}.Foerderquote_Gesamt_403741`,
				value: isFinite(foerderQuoteEachPartner) ? foerderQuoteEachPartner.toFixed(2) : 0,
			});
		}
	});

	return result;
}

function calculateGesamtSummeSkizzeLoewe(application: any) {
	let result: any = [];
	const allKostenPosition = get(application, P.Foerderantrag_Kostenpositions.path, []);

	let ProjektAusgabenGesamt = 0;
	let ProjektEinnahmenGesamt = 0;
	let BeantragteZuwendungGesamt = 0;
	let FoerderquoteGesamt = 0;

	for (let i = 0; i < allKostenPosition.length; i++) {
		const eachKostenPosition = allKostenPosition[i];

		if (get(eachKostenPosition, KP_P.Allgemein_Kostenposition.path, '') === 'Ausgabe') {
			ProjektAusgabenGesamt += parseFloat(get(eachKostenPosition, KP_P.Allgemein_Geplante_Gesamtsumme.path, '0.0'));
		}
		if (get(eachKostenPosition, KP_P.Allgemein_Kostenposition.path, '') === 'Einnahme') {
			ProjektEinnahmenGesamt += parseFloat(get(eachKostenPosition, KP_P.Allgemein_Geplante_Gesamtsumme.path, '0.0'));
		}
	}

	BeantragteZuwendungGesamt = ProjektAusgabenGesamt - ProjektEinnahmenGesamt;
	FoerderquoteGesamt = (BeantragteZuwendungGesamt / ProjektAusgabenGesamt) * 100;

	result.push(
		{
			path: P.Allgemein_Geplante_Projektausgaben.path,
			value: isFinite(ProjektAusgabenGesamt) ? ProjektAusgabenGesamt.toFixed(2) : 0,
		},
		{
			path: P.Allgemein_Eingebrachte_Eigen_Und_Drittmittel.path,
			value: isFinite(ProjektEinnahmenGesamt) ? ProjektEinnahmenGesamt.toFixed(2) : 0,
		},
		{
			path: P.Allgemein_Beantragte_Zuwendung.path,
			value: isFinite(BeantragteZuwendungGesamt) ? BeantragteZuwendungGesamt.toFixed(2) : 0,
		},
		{ path: P.Bewilligung_Foerderquote_Gesamt.path, value: isFinite(FoerderquoteGesamt) ? FoerderquoteGesamt.toFixed(2) : 0 },
	);

	return result;
}

function calculateJahresAufteilung(application: any) {
	let result: any = [];
	const projektStartJahr = get(application, P.Allgemein_Projektstart_Jahr.path);
	const projektDauer = get(application, P.Allgemein_Projektdauerin_Jahren.path, 0);

	const Jahrenaufteilung = Array.from({ length: projektDauer }, (_, index) => ({
		Projektjahr: projektStartJahr + index,
		Ausgaben: null,
		Eigen_45_Und_Drittmittel: null,
		Zuwendung: null,
		Foerderquote_403741: null,
	}));

	result.push({ path: P.Bewilligung_Jahresaufteilung.path, value: Jahrenaufteilung });
	return result;
}
